// The customer-facing embed, served at the versioned path /v1/embed.js.
//
// A customer drops one line on their page:
//
//   <div data-crowdriff-video-feed="a1b2c3"></div>
//   <script src="https://<server>/v1/embed.js" async></script>
//
// On load this scans the page once for `[data-crowdriff-video-feed]` and hydrates
// each match into its own Shadow DOM portal: the horizontal rail inline (filling
// the host div's width), and — when a clip is tapped — the immersive feed as a
// fullscreen overlay in the same shadow root. The shadow boundary encapsulates
// styles both directions, so the portal neither leaks onto nor inherits from the
// host page.
//
// The snippet carries only the opaque portal UUID. The server origin is inferred
// from this script's own <src>, and the feed is fetched from {origin}/feed/:uuid,
// so no config, folder/collection id, or token ever appears on the customer's
// page. The rail and feed are bundled in — one request, no runtime globals.
import { mountRail } from "../client/rail.js";
import { mountFeed, type FeedController } from "../client/feed.js";

const ATTR = "data-crowdriff-video-feed";

// The fullscreen overlay the feed mounts into: fixed to the viewport, inside the
// same shadow root so its styles stay encapsulated, at the top of the stack so it
// clears whatever the host page paints.
const OVERLAY_CSS = "position:fixed;inset:0;z-index:2147483647";

// `document.currentScript` is only readable while this script's top-level code
// runs — capture it now, not later inside the DOMContentLoaded callback.
const selfScript = document.currentScript as HTMLScriptElement | null;

/** The server origin these embeds talk to, taken from this script's own src. */
function resolveOrigin(): string {
  if (selfScript?.src) {
    try {
      return new URL(selfScript.src).origin;
    } catch {
      /* malformed src — fall back to the page origin */
    }
  }
  return window.location.origin;
}

function hydrate(host: HTMLElement, origin: string): void {
  // A host already carrying a shadow root was hydrated on a prior scan; skip it
  // so a script included twice never double-mounts (attachShadow would throw).
  if (host.shadowRoot) return;
  const portalUuid = host.getAttribute(ATTR);
  if (!portalUuid) return;
  const feedUrl = `${origin}/feed/${encodeURIComponent(portalUuid)}`;

  const shadow = host.attachShadow({ mode: "open" });
  const railHost = document.createElement("div");
  // Attach before mounting so the rail's <style> lands inside the shadow root
  // (mountRail keys its injected styles off the container's root node).
  shadow.appendChild(railHost);

  let overlay: HTMLDivElement | null = null;
  let controller: FeedController | null = null;

  const closeFeed = (): void => {
    controller?.destroy();
    controller = null;
    overlay?.remove();
    overlay = null;
  };

  mountRail(railHost, {
    feedUrl,
    onOpen: (startId) => {
      closeFeed(); // never stack two feeds
      const el = document.createElement("div");
      el.style.cssText = OVERLAY_CSS;
      shadow.appendChild(el);
      overlay = el;
      controller = mountFeed(el, { feedUrl, startId, onClose: closeFeed });
    },
  });
}

function hydrateAll(): void {
  const origin = resolveOrigin();
  for (const host of document.querySelectorAll<HTMLElement>(`[${ATTR}]`)) {
    hydrate(host, origin);
  }
}

// Scan once, DOMContentLoaded-safe: run now if the DOM is already parsed, else
// wait for it exactly once. An `async` script can land on either side of that.
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", hydrateAll, { once: true });
} else {
  hydrateAll();
}
